import {
  PermissionCode,
  type MaintenanceWorkOrderListQuery,
  type MaintenanceWorkOrderStatus,
} from '@gb-ticket/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  maintenanceArchivePath,
  maintenanceArchiveQueryFromSearch,
} from '@/products/maintenance/features/maintenance-archive-path';
import { MaintenanceOptionalSelect } from '@/products/maintenance/features/maintenance-filter-select';
import { MaintenanceListFooter } from '@/products/maintenance/features/maintenance-list-footer';
import {
  formatMaintenanceListDate,
  nextMaintenanceColumnSort,
} from '@/products/maintenance/features/maintenance-list-query';
import {
  maintenanceWorkOrderInstallationColumn,
  maintenanceWorkOrderScheduledColumn,
  maintenanceWorkOrderTechnicianColumn,
} from '@/products/maintenance/features/maintenance-work-order-list-columns';
import {
  isMaintenanceWorkOrderSortBy,
  MAINTENANCE_ARCHIVE_SORT_FIELDS,
  useRememberedWorkOrderSort,
  writeMaintenanceWorkOrderListSort,
} from '@/products/maintenance/features/maintenance-work-order-list-sort';
import { MaintenanceListToolbar } from '@/products/maintenance/features/maintenance-list-toolbar';
import {
  MaintenanceCustomerSearchFilter,
  MaintenanceInstallationSearchFilter,
  MaintenanceProjectSearchFilter,
} from '@/products/maintenance/features/maintenance-master-data-filters';
import { MaintenanceRecordList } from '@/products/maintenance/features/maintenance-record-list';
import { useMaintenanceRowSelection } from '@/products/maintenance/features/maintenance-row-selection';
import { MAINTENANCE_WORK_ORDER_STATUS_OPTIONS } from '@/products/maintenance/features/maintenance-status';
import { MaintenanceWorkOrderSelectionBar } from '@/products/maintenance/features/work-orders/maintenance-work-order-selection-bar';
import {
  useMaintenanceTechnicians,
  useMaintenanceWorkOrders,
} from '@/products/maintenance/features/work-orders/use-maintenance-work-orders';
import {
  maintenanceProtocolArchivePath,
  MAINTENANCE_TRASH_PATH,
} from '@/products/maintenance/maintenance-paths';

const TEMPLATE_DEBOUNCE_MS = 300;

export function MaintenanceArchivePage() {
  usePageTitle('Wartungsarchiv');
  const me = useMe();
  const canManageOrders = hasPermission(me.data, PermissionCode.MAINTENANCE_ORDERS_MANAGE);
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const parsedQuery = useMemo(() => maintenanceArchiveQueryFromSearch(search.toString()), [search]);
  const query = useRememberedWorkOrderSort(
    search.toString(),
    'archive',
    MAINTENANCE_ARCHIVE_SORT_FIELDS,
    parsedQuery,
  );
  const urlTemplate = query.templateName ?? '';
  const [templateDraft, setTemplateDraft] = useState(urlTemplate);
  const [templateSyncKey, setTemplateSyncKey] = useState(urlTemplate);
  if (templateSyncKey !== urlTemplate) {
    setTemplateSyncKey(urlTemplate);
    setTemplateDraft(urlTemplate);
  }

  const list = useMaintenanceWorkOrders(query);
  const technicians = useMaintenanceTechnicians();
  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const visibleIds = useMemo(() => items.map((order) => order.id), [items]);
  const selection = useMaintenanceRowSelection(visibleIds);
  const extraCount = [
    query.customerId,
    query.projectId,
    query.installationId,
    query.assignedToId,
    query.templateName,
  ].filter(Boolean).length;
  const filtered =
    Boolean(query.status) ||
    Boolean(query.completedFrom) ||
    Boolean(query.completedTo) ||
    extraCount > 0;

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = templateDraft.trim();
      if (trimmed === (query.templateName ?? '')) {
        return;
      }
      void navigate(
        maintenanceArchivePath({ ...query, templateName: trimmed || undefined, page: 1 }),
      );
    }, TEMPLATE_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [navigate, query, templateDraft]);

  function apply(patch: Partial<MaintenanceWorkOrderListQuery>) {
    void navigate(maintenanceArchivePath({ ...query, ...patch, page: patch.page ?? 1 }));
  }

  function resetFilters() {
    setTemplateDraft('');
    void navigate(
      maintenanceArchivePath({
        page: 1,
        pageSize: query.pageSize,
        sortBy: query.sortBy,
        sortOrder: query.sortOrder,
      }),
    );
  }

  function onColumnSort(sortBy: string) {
    if (!isMaintenanceWorkOrderSortBy(sortBy, MAINTENANCE_ARCHIVE_SORT_FIELDS)) {
      return;
    }
    const next = nextMaintenanceColumnSort(query, sortBy);
    writeMaintenanceWorkOrderListSort('archive', { sortBy, sortOrder: next.sortOrder });
    apply(next);
  }

  const technicianItems =
    technicians.data?.items.map((item) => ({
      value: item.id,
      label: item.displayName,
    })) ?? [];

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Wartungsarchiv"
        description="Aufträge und Protokolle nach Stammdaten, Monteur, Vorlage und Zeitraum filtern."
      />

      {canManageOrders ? (
        <p className="text-sm">
          <Link className="text-primary hover:underline" to={MAINTENANCE_TRASH_PATH}>
            Papierkorb öffnen
          </Link>
        </p>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Ergebnisse</h2>
        <MaintenanceListToolbar
          extraCount={extraCount}
          filtered={filtered}
          onReset={resetFilters}
          extra={
            <>
              <MaintenanceCustomerSearchFilter
                value={query.customerId ?? ''}
                onChange={(customerId) =>
                  apply({
                    customerId: customerId || undefined,
                    projectId: undefined,
                    installationId: undefined,
                  })
                }
              />
              <MaintenanceProjectSearchFilter
                customerId={query.customerId}
                value={query.projectId ?? ''}
                disabled={!query.customerId}
                onChange={(projectId) =>
                  apply({ projectId: projectId || undefined, installationId: undefined })
                }
              />
              <MaintenanceInstallationSearchFilter
                customerId={query.customerId}
                projectId={query.projectId}
                value={query.installationId ?? ''}
                disabled={!query.projectId}
                onChange={(installationId) =>
                  apply({ installationId: installationId || undefined })
                }
              />
              <MaintenanceOptionalSelect
                label="Monteur"
                id="archive-tech"
                value={query.assignedToId ?? ''}
                onValueChange={(assignedToId) => apply({ assignedToId: assignedToId || undefined })}
                items={technicianItems}
              />
              <div className="min-w-0 space-y-2">
                <Label htmlFor="archive-template">Vorlagenname</Label>
                <Input
                  id="archive-template"
                  className="min-h-11"
                  value={templateDraft}
                  placeholder="Teil des Vorlagennamens"
                  onChange={(event) => setTemplateDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void navigate(
                        maintenanceArchivePath({
                          ...query,
                          templateName: templateDraft.trim() || undefined,
                          page: 1,
                        }),
                      );
                    }
                  }}
                />
              </div>
            </>
          }
        >
          <MaintenanceOptionalSelect
            label="Status"
            id="archive-status"
            value={query.status ?? ''}
            onValueChange={(status) =>
              apply({
                status: status ? (status as MaintenanceWorkOrderStatus) : undefined,
              })
            }
            items={MAINTENANCE_WORK_ORDER_STATUS_OPTIONS}
          />
          <div className="min-w-0 space-y-2">
            <Label htmlFor="archive-from">Abgeschlossen von</Label>
            <Input
              id="archive-from"
              type="date"
              className="min-h-11"
              value={query.completedFrom ?? ''}
              onChange={(event) => apply({ completedFrom: event.target.value || undefined })}
            />
          </div>
          <div className="min-w-0 space-y-2">
            <Label htmlFor="archive-to">Abgeschlossen bis</Label>
            <Input
              id="archive-to"
              type="date"
              className="min-h-11"
              value={query.completedTo ?? ''}
              onChange={(event) => apply({ completedTo: event.target.value || undefined })}
            />
          </div>
        </MaintenanceListToolbar>
        <MaintenanceWorkOrderSelectionBar
          orders={items}
          selectedIds={selection.selectedIds}
          onClear={() => selection.clear()}
        />
        <MaintenanceRecordList
          rows={items}
          pending={list.isPending ? 'Archiv wird geladen …' : undefined}
          emptyLabel="Keine passenden Aufträge."
          filtered={filtered}
          onReset={resetFilters}
          getRowId={(order) => order.id}
          getHref={(order) => maintenanceProtocolArchivePath(order.protocol.id)}
          getTitle={(order) => order.orderNumber}
          getMeta={(order) =>
            `${order.templateName} v${order.templateVersionNumber} · ${order.customer.name}`
          }
          getStatus={(order) => order.status}
          selection={selection}
          titleHeader="Auftrag"
          titleSortKey="orderNumber"
          statusSortKey="status"
          sort={{
            sortBy: query.sortBy,
            sortOrder: query.sortOrder,
            onSort: onColumnSort,
          }}
          columns={[
            maintenanceWorkOrderInstallationColumn(),
            maintenanceWorkOrderTechnicianColumn(),
            maintenanceWorkOrderScheduledColumn(),
            {
              id: 'completed',
              header: 'Abgeschlossen',
              sortKey: 'completedAt',
              cell: (order) =>
                order.completedAt ? formatMaintenanceListDate(order.completedAt) : 'offen',
            },
          ]}
        />
        {items.length > 0 && list.data ? (
          <MaintenanceListFooter
            page={list.data.page}
            pageSize={list.data.pageSize}
            total={list.data.total}
            onPageChange={(page) => apply({ page })}
          />
        ) : null}
      </section>
    </div>
  );
}
